import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { BookingResponseDto, MeetingType } from '../../types/booking';

interface Props {
  booking: BookingResponseDto;
  accentColor?: string;
  timezone: string;
}

export default function BookingConfirmation({ booking, accentColor, timezone }: Props) {
  return (
    <div className="text-center py-6">
      <div style={accentColor ? { backgroundColor: `${accentColor}1a`, color: accentColor } : undefined} className="mx-auto w-14 h-14 rounded-full bg-strivre-lightnavy flex items-center justify-center text-strivre-navy mb-4">
        <CheckCircle2 size={28} />
      </div>
      <h3 className="text-lg font-bold text-strivre-navy">Booking Confirmed</h3>
      <p className="mt-1 text-sm text-strivre-mutedtext">A confirmation email has been sent to {booking.customerEmail}.</p>

      <div className="mt-6 bg-strivre-softgrey border border-strivre-lightnavy rounded-lg p-4 text-left space-y-2 max-w-sm mx-auto">
        <div className="flex justify-between text-sm">
          <span className="text-strivre-mutedtext">Reference Number</span>
          <span className="font-semibold text-strivre-navy">{booking.bookingReference}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-strivre-mutedtext">Service</span>
          <span className="font-semibold text-strivre-darktext">{booking.serviceName}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-strivre-mutedtext">Date</span>
          <span className="font-semibold text-strivre-darktext">{new Date(booking.bookingDate).toLocaleDateString([], { timeZone: timezone })}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-strivre-mutedtext">Time</span>
          <span className="font-semibold text-strivre-darktext">{new Date(booking.bookingDate).toLocaleTimeString([], { timeZone: timezone, hour: '2-digit', minute: '2-digit' })}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-strivre-mutedtext">
            Timezone
          </span>

          <span className="font-semibold text-strivre-darktext">
            {timezone}
          </span>
        </div>
        {/* Online meeting */}
        {booking.meetingType === MeetingType.Online && (
          <>
            <div className="flex justify-between text-sm">
              <span className="text-strivre-mutedtext">
                Meeting
          *   </span>

              <span className="font-semibold text-strivre-darktext">
                {booking.meetingProvider || "Online Meeting"}
              </span>
            </div>

            {booking.meetingUrl && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-strivre-mutedtext">
                  Meeting Link
                </span>

                <a href={booking.meetingUrl} target="_blank" rel="noopener noreferrer" className="text-strivre-accent underline">
                  Join Meeting
                </a>
              </div>
            )}
          </>
        )}

        {/* On-Site meeting */}
        {booking.meetingType === MeetingType.OnSite && (
          <>
            <div className="flex justify-between text-sm">
              <span className="text-strivre-mutedtext">
                Meeting Type
              </span>

              <span className="font-semibold text-strivre-darktext">
                On-Site
              </span>
            </div>

            {booking.meetingLocation && (
              <div className="flex justify-between gap-4 text-sm">
                <span className="text-strivre-mutedtext">
                  Location
                </span>

                <span className="max-w-[60%] text-right font-semibold text-strivre-darktext">
                  {booking.meetingLocation}
                </span>
              </div>
            )}
          </>
        )}

        {/* Phone meeting */}
        {booking.meetingType === MeetingType.Phone && (
          <div className="flex justify-between text-sm">
            <span className="text-strivre-mutedtext">
              Meeting Type
            </span>

            <span className="font-semibold text-strivre-darktext">
              Phone
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

